import React, { useMemo, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { API_BASE_URL } from "../../constants";
import ModeToggleButton from './ToggleButton'
const WIDTH = 1200;
const HEIGHT = 900;
const ROOT_X = WIDTH / 2;
const ROOT_Y = HEIGHT - 80;
const LENGTH_DECAY = 0.72;

function polarToCartesian(x, y, angle, length) {
  const rad = (angle * Math.PI) / 180;
  return {
    x: x + Math.cos(rad) * length,
    y: y - Math.sin(rad) * length,
  };
}

/**
 * Convert family-graph API -> tree structure
 * child links => vertical branches
 * spouse links => side flowers
 */
function convertGraphToTree(graphData, rootId) {
  const nodeMap = new Map();

  // create all nodes
  graphData.nodes.forEach((node) => {
    nodeMap.set(String(node.id), {
      ...node,
      children: [],
      spouses: [],
    });
  });

  // connect all generations
  graphData.links.forEach((link) => {
    const sourceId = String(link.source);
    const targetId = String(link.target);

    const sourceNode = nodeMap.get(sourceId);
    const targetNode = nodeMap.get(targetId);

    if (!sourceNode || !targetNode) return;

    // all bloodline relations go into tree
    if (
      ["child", "grandchild", "descendant"].includes(link.label)
    ) {
      sourceNode.children.push(targetNode);
    }

    // spouse handled separately
    if (link.label === "spouse") {
      sourceNode.spouses.push(targetNode);
    }
  });

  return nodeMap.get(String(rootId));
}

function buildOrganicTree(
  node,
  x,
  y,
  angle,
  length,
  depth = 0,
  nodes = [],
  links = [],
  parent = null
) {
  if (!node) return { nodes, links };

  const current = {
    ...node,
    x,
    y,
    depth,
    id: `${node.id}-${depth}-${x.toFixed(1)}`,
  };

  nodes.push(current);

  if (parent) {
    links.push({
      source: parent,
      target: current,
      width: Math.max(2, 14 - depth * 2),
    });
  }

  // ✅ render spouses beside same node
  if (node.spouses?.length) {
    node.spouses.forEach((spouse, index) => {
      const spouseX = x + (index % 2 === 0 ? 60 : -60);
      const spouseY = y + 10;

      const spouseNode = {
        ...spouse,
        x: spouseX,
        y: spouseY,
        depth,
        id: `${spouse.id}-spouse-${depth}`,
      };

      nodes.push(spouseNode);

      links.push({
        source: current,
        target: spouseNode,
        width: 3,
      });
    });
  }

  // ✅ bloodline recursion
  if (node.children?.length) {
    const count = node.children.length;
    const spread = Math.min(90, 30 + count * 12);
    const startAngle = angle - spread / 2;
    const step = count > 1 ? spread / (count - 1) : 0;

    node.children.forEach((child, index) => {
      const childAngle = startAngle + step * index;
      const next = polarToCartesian(x, y, childAngle, length);

      buildOrganicTree(
        child,
        next.x,
        next.y,
        childAngle + (index % 2 === 0 ? -8 : 8),
        length * LENGTH_DECAY,
        depth + 1,
        nodes,
        links,
        current
      );
    });
  }

  return { nodes, links };
}

function FlowerNode({ x, y, name, depth }) {
  const scale = Math.max(0.7, 1 - depth * 0.06);

  return (
    <g transform={`translate(${x}, ${y}) scale(${scale})`}>
      <circle cx="0" cy="-12" r="10" fill="#ff8fb1" />
      <circle cx="-10" cy="-2" r="10" fill="#ff9fc0" />
      <circle cx="10" cy="-2" r="10" fill="#ff9fc0" />
      <circle cx="-6" cy="-18" r="9" fill="#ffb3cb" />
      <circle cx="6" cy="-18" r="9" fill="#ffb3cb" />
      <circle r="8" fill="#f4c542" />
      <text y="28" textAnchor="middle" fontSize="11" fill="#000000">
        {name}
      </text>
    </g>
  );
}

export default function BiologicalTree() {
  const { memberId } = useParams();
  const [treeData, setTreeData] = useState(null);

  const fetchGraphData = useCallback(async (id) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}family-graph/${id}/`
      );

      const rawData = response.data.data.data;

      const graph = {
        nodes: rawData.nodes.map((n) => ({
          ...n,
          id: String(n.id),
        })),
        links: rawData.links.map((l) => ({
          ...l,
          source: String(l.source),
          target: String(l.target),
        })),
      };

      const tree = convertGraphToTree(graph, id);
      setTreeData(tree);
    } catch (error) {
      console.error("Error fetching biological tree:", error);
    }
  }, []);

  useEffect(() => {
    fetchGraphData(memberId);
  }, [memberId, fetchGraphData]);

  const { nodes, links } = useMemo(() => {
    if (!treeData) return { nodes: [], links: [] };

    return buildOrganicTree(
      treeData,
      ROOT_X,
      ROOT_Y - 180,
      90,
      170
    );
  }, [treeData]);
function FlowerNode({ x, y, name, depth, gender }) {
  const scale = Math.max(0.7, 1 - depth * 0.06);

  const isMale = gender === "M";
  const isFemale = gender === "F";

  const petal1 = isMale ? "#60a5fa" : isFemale ? "#ff8fb1" : "#c084fc";
  const petal2 = isMale ? "#93c5fd" : isFemale ? "#ff9fc0" : "#d8b4fe";
  const petal3 = isMale ? "#bfdbfe" : isFemale ? "#ffb3cb" : "#e9d5ff";
  const center = isMale ? "#1d4ed8" : isFemale ? "#f4c542" : "#7c3aed";

  return (
    <g transform={`translate(${x}, ${y}) scale(${scale})`}>
      {/* petals */}
      <circle cx="0" cy="-12" r="10" fill={petal1} />
      <circle cx="-10" cy="-2" r="10" fill={petal2} />
      <circle cx="10" cy="-2" r="10" fill={petal2} />
      <circle cx="-6" cy="-18" r="9" fill={petal3} />
      <circle cx="6" cy="-18" r="9" fill={petal3} />

      {/* flower center */}
      <circle r="8" fill={center} />

      {/* label */}
      <text y="28" textAnchor="middle" fontSize="11" fill="#000000">
        {name}
      </text>
    </g>
  );
}
  return (
    <div className="w-full overflow-auto bg-gradient-to-b from-sky-100 to-green-100 rounded-2xl">
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="w-full h-auto">

        {/* trunk */}
        <path
      d={`
        M ${ROOT_X - 40} ${ROOT_Y}
        L ${ROOT_X - 40} ${ROOT_Y - 320}
        L ${ROOT_X + 40} ${ROOT_Y - 320}
        L ${ROOT_X + 40} ${ROOT_Y}
        Z
      `}
      fill="#6b3f1d"
    />

        {/* roots */}
        {[
          [-170, 18, -245, 24],
          [-130, 40, -200, 60],
          [-80, 55, -140, 80],
          [170, 18, 245, 24],
          [130, 40, 200, 60],
          [80, 55, 140, 80],
          [-40, 70, -70, 110],
          [40, 70, 70, 110],
        ].map(([dx1, dy1, dx2, dy2], i) => (
          <path
            key={i}
            d={`M ${ROOT_X} ${ROOT_Y}
                C ${ROOT_X + dx1 * 0.4} ${ROOT_Y + dy1 * 0.4},
                  ${ROOT_X + dx1 * 0.7} ${ROOT_Y + dy1 * 0.7},
                  ${ROOT_X + dx1} ${ROOT_Y + dy1}
                S ${ROOT_X + dx2} ${ROOT_Y + dy2},
                  ${ROOT_X + dx2} ${ROOT_Y + dy2}`}
            fill="none"
            stroke="#6b3f1d"
            strokeWidth={i < 4 ? 8 : 4}
            strokeLinecap="round"
          />
        ))}

        {/* branches */}
        {links.map((link, idx) => {
          if (link.isSpouse) {
            return (
              <path
                key={idx}
                d={`M ${link.source.x} ${link.source.y}
                    C ${link.source.x + 20} ${link.source.y - 10},
                      ${link.target.x - 20} ${link.target.y - 10},
                      ${link.target.x} ${link.target.y}`}
                fill="none"
                stroke="#6b3f1d"
                strokeWidth="3"
                strokeLinecap="round"
              />
            );
          }

          const cx1 = link.source.x + (link.target.x - link.source.x) * 0.25;
          const cy1 = link.source.y - 30;
          const cx2 = link.source.x + (link.target.x - link.source.x) * 0.75;
          const cy2 = link.target.y + 20;

          return (
            <path
              key={idx}
              d={`M ${link.source.x} ${link.source.y}
                  C ${cx1} ${cy1}, ${cx2} ${cy2}, ${link.target.x} ${link.target.y}`}
              fill="none"
              stroke="#6b3f1d"
              strokeWidth={link.width}
              strokeLinecap="round"
            />
          );
        })}

        {/* canopy */}
        <path
          d={`M ${ROOT_X - 400} ${HEIGHT / 2 - 40}
              C ${ROOT_X - 460} ${HEIGHT / 2 - 180},
                ${ROOT_X - 260} ${HEIGHT / 2 - 330},
                ${ROOT_X - 80} ${HEIGHT / 2 - 300}
              C ${ROOT_X + 40} ${HEIGHT / 2 - 380},
                ${ROOT_X + 240} ${HEIGHT / 2 - 320},
                ${ROOT_X + 400} ${HEIGHT / 2 - 180}
              C ${ROOT_X + 470} ${HEIGHT / 2 - 80},
                ${ROOT_X + 460} ${HEIGHT / 2 + 120},
                ${ROOT_X + 300} ${HEIGHT / 2 + 180}
              C ${ROOT_X + 120} ${HEIGHT / 2 + 260},
                ${ROOT_X - 140} ${HEIGHT / 2 + 240},
                ${ROOT_X - 280} ${HEIGHT / 2 + 180}
              C ${ROOT_X - 430} ${HEIGHT / 2 + 120},
                ${ROOT_X - 470} ${HEIGHT / 2},
                ${ROOT_X - 400} ${HEIGHT / 2 - 40}`}
          fill="rgba(34,139,34,0.45)"
        />

        {/* flowers */}
       {nodes.map((node) => (
      <FlowerNode
        key={node.id}
        x={node.x}
        y={node.y}
        name={node.name}
        depth={node.depth}
        gender={node.gender}
      />
      ))}
      </svg>
         <ModeToggleButton
       member_id={memberId}
       setExpanded = {null}
       expanded = {null}
       mode={4} />
    </div>
  );
}